/**
 * TypeScript mirrors of the backend contracts.
 *
 * AgentConfig mirrors backend/app/schemas/agent_config.py FIELD FOR FIELD —
 * if that file changes, this one must change with it. Everything else mirrors
 * the API payload shapes in BUILD_PLAN §7 and the tables in §5.
 */

// ---------------------------------------------------------------------------
// AgentConfig enums (schemas/agent_config.py)
// ---------------------------------------------------------------------------

export type LLMModel = "gpt-4o-mini" | "gemini-2.5-flash" | "gpt-4o";
export type TTSProvider = "cartesia" | "deepgram";
export type VoiceEngine = "cascaded" | "realtime" | "gemini";
export type RealtimeModel = "gpt-realtime-2.1-mini" | "gpt-realtime-2.1";
export type RealtimeTurnDetection = "server_vad" | "semantic_vad";
export type KnowledgeMode = "auto" | "inject" | "retrieve";
export type FillerMode = "off" | "contextual";

/**
 * Gemini Live models. Must stay in sync with GeminiModel in
 * backend/app/schemas/agent_config.py. Pipecat's own default
 * (models/gemini-2.0-flash-live-001) is retired and deliberately absent.
 */
export const GEMINI_MODELS = [
  // ~3x faster to first audio than the 2.5 snapshots and far steadier
  // (ADR 0014), and the default since 2026-08-26. Still a preview model:
  // the 2.5 snapshots stay listed as the fallback if Google pulls it.
  "models/gemini-3.1-flash-live-preview",
  "models/gemini-2.5-flash-native-audio-preview-09-2025",
  "models/gemini-2.5-flash-native-audio-preview-12-2025",
] as const;
export type GeminiModel = (typeof GEMINI_MODELS)[number];

/** Fixed OpenAI realtime voice set — no cloning (surface that in the UI). */
export const REALTIME_VOICES = [
  "marin",
  "cedar",
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
] as const;
export type RealtimeVoice = (typeof REALTIME_VOICES)[number];

/** Fixed Gemini Live prebuilt voice set — likewise no cloning. */
export const GEMINI_VOICES = [
  "Puck",
  "Charon",
  "Kore",
  "Fenrir",
  "Aoede",
  "Leda",
  "Orus",
  "Zephyr",
] as const;
export type GeminiVoice = (typeof GEMINI_VOICES)[number];

/**
 * User-facing engine names. Single source of truth — the agent list, the
 * editor header, the handoff picker and validation messages all read from
 * here, so a renamed engine cannot drift between screens.
 */
export const ENGINE_LABELS: Record<VoiceEngine, string> = {
  cascaded: "Standard",
  realtime: "Realtime (OpenAI)",
  gemini: "Natural (Google)",
};

/** Short form for tight spots (badges, table cells). */
export const ENGINE_LABELS_SHORT: Record<VoiceEngine, string> = {
  cascaded: "Standard",
  realtime: "Realtime",
  gemini: "Natural",
};

/**
 * Engines that talk speech-to-speech to a provider: no separate STT/TTS stage,
 * no endpointing knob, and knowledge is looked up mid-response (§9.4).
 */
export function isSpeechToSpeech(engine: VoiceEngine): boolean {
  return engine === "realtime" || engine === "gemini";
}

export const DEFAULT_GREETING = "Hi, how can I help you today?";

// ---------------------------------------------------------------------------
// AgentConfig — persisted to agents.config (JSONB)
// ---------------------------------------------------------------------------

export interface AgentConfig {
  // §6.1 Identity (name/description/status/role are real columns, not here)
  language: string;

  // §6.2 Behavior
  system_prompt: string; // 1–8000 chars
  greeting: string; // ≤300 chars
  restrictions: string[];
  llm_model: LLMModel; // cascaded only
  temperature: number; // 0–1, cascaded only
  max_response_tokens: number; // 16–1024

  // §6.3 Voice engine & voice
  voice_engine: VoiceEngine;
  // cascaded:
  tts_provider: TTSProvider;
  voice_id: string | null; // required when cascaded
  speed: number; // 0.7–1.3
  // realtime (OpenAI):
  realtime_model: RealtimeModel;
  realtime_voice: string;
  realtime_turn_detection: RealtimeTurnDetection;
  // gemini (Google speech-to-speech):
  gemini_model: GeminiModel;
  gemini_voice: string;

  // §6.4 Conversation control
  allow_interruptions: boolean;
  interruption_min_words: number; // 1–5
  endpointing_ms: number; // 100–1000; cascaded + gemini (not realtime)
  // Instant acknowledgments (ADR 0018): a short clip in the agent's own voice,
  // matched to what the caller said, while the model composes its reply.
  filler_mode: FillerMode;
  max_call_duration_s: number; // 30–3600
  silence_timeout_s: number; // 3–60
  end_call_phrases: string[];

  // §6.5 Knowledge
  knowledge_mode: KnowledgeMode;
  retrieval_top_k: number; // 1–8

  // §6.6 Telephony
  phone_number_id: string | null;
  inbound_enabled: boolean;
  outbound_enabled: boolean;
  record_calls: boolean;

  // §6.7 Multi-agent (role is a real column on agents)
  handoff_targets: string[];
  handoff_description: string | null; // ≤300 chars
}

/** Backend defaults, mirrored — handy for building the create form. */
export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  language: "en-US",
  system_prompt: "",
  greeting: DEFAULT_GREETING,
  restrictions: [],
  llm_model: "gpt-4o-mini",
  temperature: 0.6,
  max_response_tokens: 150,
  voice_engine: "gemini",
  tts_provider: "cartesia",
  voice_id: null,
  speed: 1.0,
  realtime_model: "gpt-realtime-2.1-mini",
  realtime_voice: "marin",
  realtime_turn_detection: "semantic_vad",
  gemini_model: "models/gemini-3.1-flash-live-preview",
  gemini_voice: "Charon",
  allow_interruptions: true,
  interruption_min_words: 2,
  endpointing_ms: 600,
  filler_mode: "contextual",
  max_call_duration_s: 600,
  silence_timeout_s: 10,
  end_call_phrases: ["goodbye", "bye"],
  knowledge_mode: "auto",
  retrieval_top_k: 4,
  phone_number_id: null,
  inbound_enabled: true,
  outbound_enabled: false,
  record_calls: false,
  handoff_targets: [],
  handoff_description: null,
};

// ---------------------------------------------------------------------------
// Users & agents
// ---------------------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
}

export type AgentStatus = "draft" | "active" | "disabled";
export type AgentRole = "standalone" | "router" | "specialist";

export interface Agent {
  id: string;
  name: string;
  description: string | null;
  status: AgentStatus;
  role: AgentRole;
  config: AgentConfig;
  created_at: string;
  updated_at: string;
  /** Non-blocking validation warnings from the backend (context budget, …). */
  warnings?: string[];
}

/**
 * One row from GET /api/agents — mirrors backend AgentSummary: a flat list
 * shape without the full config (engine/number/7d counts are pre-resolved).
 */
export interface AgentSummary {
  id: string;
  name: string;
  description: string | null;
  status: AgentStatus;
  role: AgentRole;
  voice_engine: VoiceEngine;
  phone_e164: string | null;
  calls_7d: number;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Payload for POST /api/agents and PATCH /api/agents/{id}. */
export interface AgentWrite {
  name?: string;
  description?: string | null;
  status?: AgentStatus;
  role?: AgentRole;
  config?: AgentConfig;
}

// ---------------------------------------------------------------------------
// Documents (knowledge)
// ---------------------------------------------------------------------------

export type DocumentStatus = "pending" | "processing" | "ready" | "failed";

/** POST /api/agents/{id}/documents returns this minimal ack, not the full row. */
export interface DocumentUploadResponse {
  document_id: string;
  status: DocumentStatus;
}

export interface DocumentInfo {
  id: string;
  agent_id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  status: DocumentStatus;
  error: string | null;
  token_count: number | null;
  chunk_count: number | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Phone numbers
// ---------------------------------------------------------------------------

export type NumberDirection = "inbound" | "outbound" | "both";

export interface PhoneNumber {
  id: string;
  agent_id: string | null; // null = owned but unassigned
  e164: string;
  country: string | null;
  capabilities: Record<string, boolean> | null;
  direction: NumberDirection;
  created_at: string;
}

/** One row from GET /api/numbers/available (Twilio search). */
export interface AvailableNumber {
  phone_number: string; // E.164
  friendly_name?: string | null;
  locality?: string | null;
  region?: string | null;
  country: string;
  capabilities?: Record<string, boolean> | null;
}

// ---------------------------------------------------------------------------
// Calls & transcripts
// ---------------------------------------------------------------------------

export type CallDirection = "inbound" | "outbound" | "browser_test";
export type CallStatus = "initiating" | "in_progress" | "completed" | "failed" | "no_answer";
export type EndReason = "caller_hangup" | "agent_hangup" | "error" | "timeout";
export type TurnRole = "user" | "agent" | "system";

/** calls.metrics JSONB — engine is always recorded; the rest is per-engine.
 * The runtime writes the NESTED shape (metrics.py snapshot()); metric v2 adds
 * orphan segregation, stage decomposition, and the per-call config stamp. */
export interface VoiceToVoiceStats {
  avg_ms?: number | null;
  p95_ms?: number | null;
  samples?: number;
  orphaned_samples?: number;
  orphaned_avg_ms?: number | null;
  orphaned_p95_ms?: number | null;
}

export interface StageStats {
  avg_ms?: number;
  p95_ms?: number;
  count?: number;
}

/** One turn's exact timeline (calls.metrics.turns). t_* are ms offsets on the
 * pipeline clock from the call's first frame; v2v = user_stop→speaking,
 * server_reply = user_stop→first model audio byte (the S2S round trip),
 * output_path = first audio→playback start. */
export interface TurnLatencyDetail {
  turn: number;
  t_user_stopped_ms?: number | null;
  t_first_sound_ms?: number | null;
  t_first_audio_ms?: number | null;
  t_bot_speaking_ms?: number | null;
  v2v_ms?: number | null;
  /** user_stop → first audible response (a filler ack clip when `filler`). */
  first_sound_ms?: number | null;
  server_reply_ms?: number | null;
  output_path_ms?: number | null;
  filler?: boolean;
  orphaned?: boolean;
}

export interface CallMetrics {
  engine?: VoiceEngine;
  call?: { endpointing_ms?: number; model?: string; transport?: string };
  voice_to_voice?: VoiceToVoiceStats | null;
  interruptions?: number;
  interruptions_by_source?: Record<string, number>;
  orphaned_turns?: number;
  suspected_turn_splits?: number;
  /** Estimated last-word→first-sound at the caller's ear (ADR 0018). */
  felt_estimate_ms?: number | null;
  filler_acks?: number;
  stages?: Record<string, StageStats>;
  turns?: TurnLatencyDetail[];
  [key: string]: unknown;
}

export interface CallSummary {
  id: string;
  agent_id: string;
  direction: CallDirection;
  from_number: string | null;
  to_number: string | null;
  twilio_call_sid: string | null;
  status: CallStatus;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  end_reason: EndReason | null;
  summary?: string | null;
  cost_estimate: number | string | null; // NUMERIC may serialize as string
  metrics?: CallMetrics | null;
}

/** transcript_turns.metadata JSONB. */
export interface TurnMetadata {
  interrupted?: boolean;
  retrieved_chunk_ids?: string[];
  latency_ms?: number;
  /** speech end → first model audio byte (the S2S round trip). */
  server_reply_ms?: number;
  /** first model audio byte → playback start at the transport. */
  output_path_ms?: number;
  [key: string]: unknown;
}

export interface TranscriptTurn {
  id: number; // BIGSERIAL
  turn_index: number;
  role: TurnRole;
  content: string;
  started_at_ms: number | null; // offset from call start
  duration_ms: number | null;
  agent_id: string | null; // which sub-agent spoke (multi-agent)
  metadata: TurnMetadata | null;
}

export interface CallDetail extends CallSummary {
  summary: string | null;
  metrics: CallMetrics | null;
  turns: TranscriptTurn[];
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface CallListFilters {
  agent_id?: string;
  from?: string; // ISO date
  to?: string; // ISO date
  status?: CallStatus;
  direction?: CallDirection;
  page?: number;
  page_size?: number;
}

// ---------------------------------------------------------------------------
// Analytics (§14) — SQL aggregates, per agent
// ---------------------------------------------------------------------------

/** Mirrors backend VolumePoint — sparse: days with zero calls are omitted. */
export interface AnalyticsVolumePoint {
  day: string; // ISO date
  inbound: number;
  outbound: number;
}

/** Mirrors backend EngineStats. */
export interface EngineAnalytics {
  engine: string; // 'cascaded' | 'realtime' | 'unknown'
  calls: number;
  avg_duration_s: number | null;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
  interruptions_per_call: number | null;
  cost_total: number;
  avg_cost_per_call: number | null;
}

export interface BrowserTestStats {
  calls: number;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
}

/** Mirrors backend AnalyticsOut (phone calls; browser tests reported separately). */
export interface Analytics {
  range: string; // 1d | 7d | 30d
  since: string;
  total_calls: number;
  volume_by_day: AnalyticsVolumePoint[];
  avg_duration_s: number | null;
  p95_duration_s: number | null;
  end_reasons: Record<string, number>;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
  interruptions_per_call: number | null;
  cost_total: number;
  avg_cost_per_call: number | null;
  by_engine: EngineAnalytics[];
  browser_tests: BrowserTestStats;
}

// ---------------------------------------------------------------------------
// Auth & test sessions
// ---------------------------------------------------------------------------

export interface AuthResponse {
  user: User;
  access_token: string;
  refresh_token: string;
}

export interface RefreshResponse {
  access_token: string;
  refresh_token?: string; // rotation — may or may not be returned
}

export interface TestSession {
  session_id: string;
  /** SmallWebRTC SDP signaling endpoint — null if the runtime is WS-only. */
  offer_url: string | null;
  ws_url: string | null;
  ice_servers: RTCIceServer[];
}
